import { DateTime } from '@ksyos/date-time';
import { uniqBy } from 'lodash';
import { useState } from 'react';
import type { IProcess } from '@frontend/components/interfaces/process';
import type { ValidationErrors } from '@frontend/utils/validation';
import { RequestStatus } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { requestEditRequesterSchema } from '@common/validation/request';
import { Button, ButtonContainer, Dropdown, DropdownSearch } from '@frontend/components/elements/common';
import { DisplayMessage } from '@frontend/components/elements/DisplayMessage';
import { FieldSet } from '@frontend/components/elements/fieldset/FieldSet';
import { FormGroup } from '@frontend/components/elements/forms/FormGroup';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';

interface IChangeRequesterProps {
    currentOrganizationId: string;
    currentRequesterId: string;
    process: IProcess;
    reload: () => Promise<void>;
    serviceId: string;
}

interface IEmployment {
    id: string;
    organization: {
        id: string;
        name: string;
    };
    practitioner: {
        id: string;
        nameFamily: string;
    };
}

interface IRequester {
    requesterOrganizationId: string | null;
    requesterPractitionerId: string | null;
}

export function ChangeRequester({
    process,
    serviceId,
    currentRequesterId,
    currentOrganizationId,
    reload,
}: IChangeRequesterProps) {
    const [requesterOptions, setRequesterOptions] = useState<IEmployment[]>([]);
    const [requester, setRequester] = useState<IRequester>({
        requesterOrganizationId: null,
        requesterPractitionerId: null,
    });
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [message, setMessage] = useState<string | null>(null);

    return (
        <FieldSet>
            <FormGroup hasErrors={errors.has('requesterOrganizationId') || errors.has('recipientOrganizationId')}>
                <DropdownSearch
                    error={errors.get('requesterOrganizationId') || errors.get('recipientOrganizationId')}
                    loadOptions={async (input) => await getOrganizationBySearchQuery(input)}
                    onChange={async (value) => await handleChange('requesterOrganizationId', value)}
                    onDoValidate={async (value) => await validateOne('requesterOrganizationId', value)}
                    placeholder={translateKey('common.selectItem', translateKey('organization.organization'))}
                    value={requester.requesterOrganizationId}
                />
            </FormGroup>
            <FormGroup hasErrors={errors.has('requesterPractitionerId') || errors.has('recipientPractitionerId')}>
                <Dropdown
                    error={errors.get('requesterPractitionerId') || errors.get('recipientPractitionerId')}
                    onChange={async (value) => await handleChange('requesterPractitionerId', value)}
                    onDoValidate={async (value) => await validateOne('requesterPractitionerId', value)}
                    placeholder={translateKey('common.selectItem', translateKey('practitioner.practitioner'))}
                    selectOptions={getRequesterPractitionersOptions()}
                    value={requester.requesterPractitionerId}
                />
            </FormGroup>
            {message && (
                <DisplayMessage
                    message={message}
                    messageType="info"
                    onClose={async () => setMessage(null)}
                />
            )}
            <ButtonContainer
                buttons={[
                    <Button
                        color="secondary"
                        disabled={!requester.requesterOrganizationId || !requester.requesterPractitionerId}
                        onClick={async () => await saveServiceConsumer()}
                    >
                        {translateKey('request.assign')}
                    </Button>,
                ]}
                noMargin
            />
        </FieldSet>
    );

    async function getOrganizationBySearchQuery(searchOrganization: string) {
        const requesterData = await fetchRequesterData({
            serviceId,
            searchOrganization,
            onlyEmploymentBasics: true,
        });

        return uniqBy(
            requesterData.map((option: IEmployment) => ({
                value: option.organization.id,
                label: option.organization.name,
            })),
            'value'
        );
    }

    async function getPractitionersByOrganizationId(organizationId: string | null) {
        let options: IEmployment[] = [];

        if (organizationId !== null) {
            const requesterData = await fetchRequesterData({
                serviceId,
                organizationId,
            });

            options = requesterData.filter(
                (item) =>
                    !(item.practitioner.id === currentRequesterId && item.organization.id === currentOrganizationId)
            );
        }

        return options;
    }

    async function fetchRequesterData(urlParams: { [key: string]: string | boolean } = {}): Promise<IEmployment[]> {
        const requests = [];

        {
            const builder = new UrlBuilder('/api/serviceConsumers');

            Object.entries(urlParams).forEach((urlParam: [string, string | boolean]) => {
                builder.add(...urlParam);
            });

            if (process.status === RequestStatus.completed) {
                builder.add('searchIsActiveOrWasActiveAtDate', DateTime.from(process.created_at).format('YYYY-MM-DD'));
            } else {
                builder.add('isActive', true);
            }

            builder.add('isOrganizationActive', true);

            requests.push(fetchJson(builder.build(), 'GET'));
        }

        {
            const builder = new UrlBuilder('/api/employments/generalPractitioners');

            ['searchOrganization', 'organizationId'].forEach((param) => {
                if (urlParams[param]) {
                    builder.add({ [param]: urlParams[param] });
                }
            });

            if (process.status === RequestStatus.completed) {
                builder.add('searchIsActiveOrWasActiveAtDate', DateTime.from(process.created_at).format('YYYY-MM-DD'));
            }

            requests.push(fetchJson(builder.build(), 'GET'));
        }

        const responses = await Promise.all(requests);

        const options = [].concat(
            ...responses.map((response) =>
                response.json.items.map((item: { employment?: IEmployment }) =>
                    item.employment ? item.employment : item
                )
            )
        );

        return uniqBy(options, 'id');
    }

    async function handleChange<K extends keyof IRequester>(name: K, value: IRequester[K]) {
        if (name === 'requesterOrganizationId') {
            setRequester({ requesterOrganizationId: value, requesterPractitionerId: null });
            setRequesterOptions(await getPractitionersByOrganizationId(value));
        } else {
            setRequester({ ...requester, requesterPractitionerId: value });
        }
    }

    async function validateOne(name: keyof IRequester, value: string | null) {
        setErrors(updateValidationErrorsForOne(requestEditRequesterSchema, value, errors, name));
    }

    function getRequesterPractitionersOptions() {
        const options = requesterOptions.filter(
            (requesterOption) =>
                !(
                    requesterOption.practitioner.id === currentRequesterId &&
                    requesterOption.organization.id === currentOrganizationId
                )
        );

        return options.map((requesterOption: IEmployment) => ({
            value: requesterOption.practitioner.id,
            text: getDisplayName(requesterOption.practitioner),
        }));
    }

    async function saveServiceConsumer() {
        setMessage(null);
        const validation = validate(requestEditRequesterSchema, requester);

        setErrors(validation.errors);

        if (validation.errors.size > 0) {
            return;
        }

        await fetchJson(`/api/requests/${process.id}/updateRequester`, 'POST', validation.item);
        setMessage(translateKey('serviceProvider.changedSuccess'));
        await reload();
    }
}
